import api from '../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FaFilter } from 'react-icons/fa'
import { FiInbox } from 'react-icons/fi'
import { VscClearAll } from 'react-icons/vsc'

import Input from "../../shared/components/form/input"
import useMyPermission from '../../hooks/useMyPermission'
import ListClientRow from './ListRow/ListClientRow'
import Modal from '../../shared/modal/Modal'
import NewClient from './NewClient'
import ClientFilterModal from './ClientFilterModal'
import Button from '../../shared/components/Button/Button'

const EMPTY_FILTERS = { personType: 'all', generatorStatus: 'all', seller: '' }

function MyClient() {

    const [token] = useState(localStorage.getItem('token') || '')
    const [clients, setClients] = useState([])
    const [modalNew, setModalNew] = useState(false)
    const [modalFilter, setModalFilter] = useState(false)
    const [search, setSearch] = useState("")
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [load, setLoad] = useState(false)

    const [myPermission, setMyPermission] = useState({ group: "" })
    const { checkPermission } = useMyPermission()
    const myPermissionNew    = checkPermission(myPermission, "myClient_Create")
    const myPermissionView   = checkPermission(myPermission, "myClient_View")
    const myPermissionEdit   = checkPermission(myPermission, "myClient_Edit")
    const myPermissionDelete = checkPermission(myPermission, "myClient_Delete")

    const myPermissionGeneratorLinkView = checkPermission(myPermission, "clientGenerator_View")
    const myPermissionGeneratorLinkEdit = checkPermission(myPermission, "clientGenerator_Edit")
    const myPermissionSellerLinkView    = checkPermission(myPermission, "clientSeller_View")
    const myPermissionSellerLinkEdit    = checkPermission(myPermission, "clientSeller_Edit")

    const getAll = () => {
        api.get(`/client/getallmy`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            if (response) {
                setClients(response.data.clients)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    useEffect(() => { getAll() }, [token])

    const sellerOptions = useMemo(() => {
        const map = new Map()
        clients.forEach((c) => (c.associatedUsers || []).forEach((u) => { if (u && u._id) map.set(String(u._id), u.name) }))
        return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
    }, [clients])

    const filter = useMemo(() => {
        return clients.filter((item) => {
            const matchSearch = (item.name || '').toLowerCase().includes(search.toLowerCase())
            const matchPersonType = filters.personType === 'all' || item.personType === filters.personType
            const generatorCount = (item.generators || []).length
            const matchGenerator =
                filters.generatorStatus === 'all'  ? true :
                filters.generatorStatus === 'with' ? generatorCount > 0 :
                                                      generatorCount === 0
            const matchSeller = !filters.seller || (item.associatedUsers || []).some((u) => String(u._id) === String(filters.seller))
            return matchSearch && matchPersonType && matchGenerator && matchSeller
        })
    }, [clients, search, filters])

    const hasActiveFilter = !!search || filters.personType !== 'all' || filters.generatorStatus !== 'all' || !!filters.seller

    const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }))
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch('') }

    if (!load) {
        return <div className="pg-loading">Carregando clientes...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Meus Clientes</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionNew && (
                                <Button variant="new" onClick={() => setModalNew(true)}>Cadastrar</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar cliente..."
                                    value={search}
                                    handleOnChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'cliente' : 'clientes'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {filter.length} {filter.length === 1 ? 'cliente encontrado' : 'clientes encontrados'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <ListClientRow
                        sellerEnabled={true}
                        fakeDelete={true}
                        dataArray={filter}
                        handleOnSuccess={getAll}
                        myPermissionEdit={myPermissionEdit}
                        myPermissionDelete={myPermissionDelete}
                        myPermissionView={myPermissionView}
                        myPermissionGeneratorView={myPermissionGeneratorLinkView}
                        myPermissionGeneratorEdit={myPermissionGeneratorLinkEdit}
                        myPermissionSellerView={myPermissionSellerLinkView}
                        myPermissionSellerEdit={myPermissionSellerLinkEdit}
                    />
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhum cliente encontrado</p>
                        <p className="pg-empty-subtitle">
                            {clients.length === 0
                                ? 'Você ainda não está associado a nenhum cliente.'
                                : 'Tente ajustar a busca ou os filtros.'}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNew}
                changeStatus={setModalNew}
                w={80}
                content={
                    <NewClient
                        sellerEnabled={true}
                        btCancelAction={() => setModalNew(false)}
                        handleOnSuccess={() => { setModalNew(false); getAll() }}
                    />
                }
            />

            {modalFilter && (
                <ClientFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                    sellerOptions={sellerOptions}
                    showStatusFilter={false}
                    showGeneratorFilter={myPermissionGeneratorLinkView}
                    showSellerFilter={myPermissionSellerLinkView}
                />
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar clientes</p>
        </div>
    )
}

export default MyClient
